import React, { useState } from 'react';
import { Truck, Check } from 'lucide-react';
import MaskedInput from '../common/MaskedInput';
import Button from '../common/Button';
import { shippingService } from '../../services/shippingService';
import { useCart } from '../../hooks/useCart';
import { formatPrice } from '../../utils/priceCalculator';

const ShippingField = () => {
  const { items, shipping, setShippingOption, clearShipping, coupon } = useCart();
  const [cep, setCep] = useState(shipping?.cep || '');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const freeShipping = coupon?.type === 'FREE_SHIPPING' || coupon?.freeShipping;

  const handleQuote = async () => {
    const clean = cep.replace(/\D/g, '');
    if (clean.length !== 8) {
      setError('Informe um CEP válido (8 dígitos).');
      return;
    }
    if (!items.length) {
      setError('Adicione produtos à sacola antes de calcular o frete.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await shippingService.quote({
        cep: clean,
        items: items.map((it) => ({
          productId: it.productId,
          variationId: it.variationId,
          quantity: it.quantity
        }))
      });
      setOptions(data.services || []);
      // Se nenhum frete escolhido ainda, escolhe o mais barato.
      if (!shipping && data.services?.length) {
        const cheapest = [...data.services].sort((a, b) => a.price - b.price)[0];
        setShippingOption({ ...cheapest, cep: data.cep });
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Falha ao calcular frete.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (opt) => {
    setShippingOption({ ...opt, cep: cep.replace(/\D/g, '').replace(/(\d{5})(\d{3})/, '$1-$2') });
  };

  const handleClear = () => {
    clearShipping();
    setOptions([]);
    setError('');
  };

  return (
    <div>
      <label className="block text-xs font-semibold text-app-text-secondary mb-1.5 uppercase tracking-wide">
        Frete
      </label>
      <div className="flex gap-2">
        <MaskedInput
          mask="cep"
          value={cep}
          onChange={(e) => setCep(e.target.value)}
          placeholder="00000-000"
          leftIcon={<Truck size={16} />}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleQuote();
            }
          }}
        />
        <Button
          variant="subtle"
          onClick={handleQuote}
          loading={loading}
          disabled={!cep || cep.replace(/\D/g, '').length !== 8}
        >
          Calcular
        </Button>
      </div>
      {error && <p className="mt-1 text-xs text-error">{error}</p>}

      {options.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {options.map((opt) => {
            const selected = shipping?.provider === opt.provider;
            const finalPrice = freeShipping ? 0 : opt.price;
            return (
              <li key={opt.provider}>
                <button
                  type="button"
                  className={`w-full flex items-center justify-between gap-2 rounded-lg border p-2.5 text-left transition-colors ${
                    selected
                      ? 'border-primary bg-primary/5'
                      : 'border-app-border hover:border-primary/40'
                  }`}
                  onClick={() => handleSelect(opt)}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {selected && <Check size={16} className="text-primary shrink-0" />}
                    <div className="min-w-0">
                      <p className="font-medium text-app-text truncate">{opt.name}</p>
                      <p className="text-xs text-app-text-secondary">
                        Em até {opt.etaDays} {opt.etaDays === 1 ? 'dia útil' : 'dias úteis'}
                      </p>
                    </div>
                  </div>
                  <p className="font-semibold text-primary shrink-0">
                    {freeShipping ? (
                      <span className="text-success">Grátis</span>
                    ) : (
                      formatPrice(finalPrice)
                    )}
                  </p>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {shipping && options.length === 0 && (
        <div className="mt-2 flex items-center justify-between rounded-lg border border-primary/30 bg-primary/5 p-2.5">
          <div>
            <p className="font-medium text-app-text">{shipping.name}</p>
            <p className="text-xs text-app-text-secondary">
              CEP {shipping.cep} • {shipping.etaDays} dia(s) úteis
            </p>
          </div>
          <button
            type="button"
            className="text-xs text-app-text-secondary underline hover:text-error"
            onClick={handleClear}
          >
            Trocar
          </button>
        </div>
      )}
    </div>
  );
};

export default ShippingField;
